import {graphql} from 'gatsby'
import {m as motion} from 'motion/react'
import React from 'react'
import {useInView} from 'react-intersection-observer'
import styled from 'styled-components'

import Button from '@/components/button'
import Image from '@/components/image'
import PrismicLink from '@/components/prismic-link'
import RichText from '@/components/rich-text'
import {text, Text} from '@/components/text'
import {Section} from '@/styles/grid'
import {headerSizes, tokens, colors} from '@/theme'
import mq from '@/utils/media-query'

import type {ComparisonFragment} from '@/graphql-types'

const ANIMATION_DURATION = 1 // seconds
const SECONDARY_BAR_DELAY = 0.5 // seconds

export interface Props {
    content: ComparisonFragment
}

export const Comparison = ({content}: Props) => {
    return (
        <Wrapper>
            <MainArea>
                <Heading as="h2" $variant="heading-large">
                    {content.primary.title}
                </Heading>

                <Charts>
                    {content.items.map((item, index) => (
                        <Chart key={`animated-chart-${index}`} item={item} />
                    ))}
                </Charts>
            </MainArea>
            <Card>
                {content.primary.card_logo && <CardLogo image={content.primary.card_logo} />}
                <CardCopy>
                    <RichText
                        $variant="paragraph-base"
                        content={content.primary.card_body.richText}
                    />
                    <Button $variant="outline" as={PrismicLink} {...content.primary.cta_link}>
                        {content.primary.cta_label}
                    </Button>
                </CardCopy>
            </Card>
        </Wrapper>
    )
}

interface ChartProps {
    item: ComparisonFragment['items'][number]
}

const Chart = ({item}: ChartProps) => {
    const [ref, isInView] = useInView({
        threshold: 0.5,
        triggerOnce: true,
    })

    return (
        <ChartWrapper ref={ref}>
            <ChartLabel as="h3">{item.comparison_label}</ChartLabel>
            <AnimatedBar
                label={item.item_1_label}
                percentage={item.item_1_percentage}
                color="pink"
                inView={isInView}
                delay={0}
            />
            <AnimatedBar
                label={item.item_2_label}
                percentage={item.item_2_percentage}
                color="black"
                inView={isInView}
                delay={SECONDARY_BAR_DELAY}
            />
        </ChartWrapper>
    )
}

interface AnimatedBarProps {
    label: string
    percentage: number
    color: 'pink' | 'black'
    inView: boolean
    delay: number // seconds
}

const AnimatedBar = ({label, percentage, color, inView, delay}: AnimatedBarProps) => (
    <BarWrapper>
        <BarLabel
            $variant="paragraph-large"
            $color={color === 'pink' ? 'default' : 'inverted'}
            $isVisible={inView}
            $delay={delay}
        >
            {label}
        </BarLabel>
        <Bar
            variants={{
                initial: {scaleX: 0},
                animate: {
                    scaleX: 1,
                    transition: {
                        duration: ANIMATION_DURATION,
                        when: 'beforeChildren',
                        ease: 'easeOut',
                        delay,
                    },
                },
            }}
            initial="initial"
            animate={inView ? 'animate' : 'initial'}
            $percentage={percentage}
            $color={color}
        >
            <Text
                forwardedAs={motion.p}
                $variant="title-base"
                $color={color === 'pink' ? 'default' : 'inverted'}
                variants={{
                    initial: {opacity: 0},
                    animate: {
                        opacity: 1,
                        transition: {
                            duration: ANIMATION_DURATION,
                            ease: 'easeOut',
                        },
                    },
                }}
            >
                {percentage}%
            </Text>
        </Bar>
    </BarWrapper>
)

const Wrapper = styled(Section)`
    display: grid;
    gap: ${tokens.spacing40};
    align-items: start;

    ${mq.min('md')} {
        gap: 80px;
        grid-template-columns: 1fr auto;
    }
`

const MainArea = styled.div`
    display: grid;
    gap: ${tokens.spacing40};

    ${mq.min('md')} {
        gap: 80px;
    }
`

const Heading = styled(Text)`
    max-width: 600px;
`

const Card = styled.aside`
    display: grid;
    padding: ${tokens.spacing24};
    background: ${tokens.shade200};
    border-radius: ${tokens.arc20};
    gap: ${tokens.spacing20};

    ${mq.min('md')} {
        padding: ${tokens.spacing40};
    }

    ${mq.between('sm', 'md')} {
        grid-template-columns: 6fr 4fr;
    }

    ${mq.min('md')} {
        max-width: 300px;
        position: sticky;
        top: ${headerSizes.heightLarge + 80}px;
    }
`

const CardLogo = styled(Image)`
    justify-self: center;

    ${mq.between('sm', 'md')} {
        order: 3;
        width: 200px;
    }
`

const CardCopy = styled.div`
    display: grid;
    gap: ${tokens.spacing20};

    ${mq.between('sm', 'md')} {
        justify-items: start;
        align-content: center;
    }
`

const Charts = styled.div`
    display: grid;
    gap: ${tokens.spacing40};
`

const BarWrapper = styled.div`
    position: relative;
    display: grid;
    gap: ${tokens.spacing10};
`

const ChartWrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing12};

    ${mq.min('md')} {
        gap: ${tokens.spacing20};
    }
`

const ChartLabel = styled.p`
    ${text({$variant: 'title-base'})};

    ${mq.min('md')} {
        ${text({$variant: 'paragraph-large'})};
    }
`

interface BarLabelProps {
    $isVisible: boolean
    $delay: number
}

const BarLabel = styled(Text)<BarLabelProps>`
    ${mq.max('sm')} {
        color: ${colors.text.default};
    }

    ${mq.min('sm')} {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        left: ${tokens.spacing20};
        z-index: 1;
        opacity: ${(props) => (props.$isVisible ? 1 : 0)};
        transition: opacity ${tokens.slowIn} ${(props) => props.$delay + ANIMATION_DURATION / 4}s;
        will-change: opacity;
    }
`

interface BarProps {
    $color: 'pink' | 'black'
    $percentage: number
}

const Bar = styled(motion.create('div'))<BarProps>`
    height: ${tokens.spacing60};
    display: flex;
    justify-content: flex-end;
    width: ${(props) => props.$percentage}%;
    background: ${(props) => (props.$color === 'pink' ? tokens.pink400 : tokens.shade900)};
    padding: 10px 20px;
    border-radius: ${tokens.arc8};
    align-items: center;
    transform-origin: left;
`

export const comparisonFragment = graphql`
    fragment Comparison on PrismicSlicePageDataBodyComparison {
        __typename
        primary {
            title
            card_body {
                richText
            }
            cta_label
            cta_link {
                ...PrismicLink
            }
            card_logo {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
        }
        items {
            comparison_label
            item_1_label
            item_2_label
            item_1_percentage
            item_2_percentage
        }
    }
`
